import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Upload, Music, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import './AdminStyles.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TrackManagement = () => {
  const [tracks, setTracks] = useState([]);
  const [releases, setReleases] = useState([]);
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTrack, setEditingTrack] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    release_id: '',
    artist_id: '',
    artist_name: '',
    duration: 180,
    track_number: 1,
    preview_start: 30,
    price: 1.19,
    discount_price: '',
    is_available: true
  });

  const getToken = () => localStorage.getItem('adminToken');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    const token = getToken();
    try {
      const [tracksRes, releasesRes, artistsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/tracks`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/releases`),
        fetch(`${API_URL}/api/artists`)
      ]);

      if (tracksRes.ok) setTracks(await tracksRes.json());
      if (releasesRes.ok) setReleases(await releasesRes.json());
      if (artistsRes.ok) setArtists(await artistsRes.json());
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = getToken();

    // Find artist name
    const artist = artists.find(a => a.id === formData.artist_id);
    const dataToSend = {
      ...formData,
      artist_name: artist?.name || formData.artist_name,
      price: parseFloat(formData.price) || 1.19,
      discount_price: formData.discount_price ? parseFloat(formData.discount_price) : null,
      duration: parseInt(formData.duration) || 180,
      track_number: parseInt(formData.track_number) || 1,
      preview_start: parseInt(formData.preview_start) || 30
    };

    try {
      const url = editingTrack 
        ? `${API_URL}/api/admin/tracks/${editingTrack.id}`
        : `${API_URL}/api/admin/tracks`;
      
      const res = await fetch(url, {
        method: editingTrack ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(dataToSend)
      });

      if (!res.ok) throw new Error('Failed to save track');

      toast.success(editingTrack ? 'Track updated' : 'Track created');
      closeModal();
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (trackId) => {
    if (!window.confirm('Delete this track?')) return;
    
    const token = getToken();
    try {
      const res = await fetch(`${API_URL}/api/admin/tracks/${trackId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Failed to delete');
      
      toast.success('Track deleted');
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleAudioUpload = async (trackId, file, format) => {
    const token = getToken();
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('format', format);

      const res = await fetch(`${API_URL}/api/admin/tracks/${trackId}/upload-audio`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (!res.ok) throw new Error('Upload failed');
      
      toast.success(`${format.toUpperCase()} file uploaded successfully`);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const openModal = (track = null) => {
    if (track) {
      setEditingTrack(track);
      setFormData({
        title: track.title || '',
        release_id: track.release_id || '',
        artist_id: track.artist_id || '',
        artist_name: track.artist_name || '',
        duration: track.duration || 180,
        track_number: track.track_number || 1,
        preview_start: track.preview_start || 30,
        price: track.price || 1.19,
        discount_price: track.discount_price || '',
        is_available: track.is_available !== false
      });
    } else {
      setEditingTrack(null);
      setFormData({
        title: '',
        release_id: '',
        artist_id: '',
        artist_name: '',
        duration: 180,
        track_number: 1,
        preview_start: 30,
        price: 1.19,
        discount_price: '',
        is_available: true
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTrack(null);
  };

  const handleReleaseChange = (releaseId) => {
    const release = releases.find(r => r.id === releaseId);
    if (release) {
      const artist = artists.find(a => a.name === release.artist_name);
      setFormData(prev => ({
        ...prev,
        release_id: releaseId,
        artist_id: artist?.id || '',
        artist_name: release.artist_name
      }));
    }
  };

  if (loading) {
    return <div className="admin-page"><div className="loading-spinner">Loading...</div></div>;
  }

  return (
    <div className="admin-page" data-testid="track-management">
      <div className="admin-header">
        <div>
          <h1>Track Management</h1>
          <p className="admin-subtitle">{tracks.length} tracks</p>
        </div>
        <button className="btn btn-primary" onClick={() => openModal()}>
          <Plus size={18} /> Add Track
        </button>
      </div>

      {tracks.length === 0 ? (
        <div className="empty-state">
          <Music size={48} />
          <h3>No tracks yet</h3>
          <p>Add tracks to your releases to enable store purchases</p>
        </div>
      ) : (
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Title</th>
                <th>Artist</th>
                <th>Release</th>
                <th>Price</th>
                <th>MP3</th>
                <th>WAV</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tracks.map((track) => {
                const release = releases.find(r => r.id === track.release_id);
                return (
                  <tr key={track.id}>
                    <td>{track.track_number}</td>
                    <td>{track.title}</td>
                    <td>{track.artist_name}</td>
                    <td>{release?.title || '-'}</td>
                    <td>
                      {track.discount_price ? (
                        <span>
                          <s style={{color: 'var(--light-grey)'}}>${track.price}</s>{' '}
                          <span style={{color: '#22c55e'}}>${track.discount_price}</span>
                        </span>
                      ) : (
                        `$${track.price}`
                      )}
                    </td>
                    <td>
                      {track.audio_path || track.audio_path_mp3 ? (
                        <span style={{color: '#22c55e'}}>✓</span>
                      ) : (
                        <label className="upload-audio-btn">
                          <input
                            type="file"
                            accept=".mp3,audio/mpeg"
                            style={{display: 'none'}}
                            onChange={(e) => {
                              if (e.target.files[0]) {
                                handleAudioUpload(track.id, e.target.files[0], 'mp3');
                              }
                            }}
                            disabled={uploading}
                          />
                          <Upload size={14} />
                        </label>
                      )}
                    </td>
                    <td>
                      {track.audio_path_wav ? (
                        <span style={{color: '#22c55e'}}>✓</span>
                      ) : (
                        <label className="upload-audio-btn">
                          <input
                            type="file"
                            accept=".wav,audio/wav"
                            style={{display: 'none'}}
                            onChange={(e) => {
                              if (e.target.files[0]) {
                                handleAudioUpload(track.id, e.target.files[0], 'wav');
                              }
                            }}
                            disabled={uploading}
                          />
                          <Upload size={14} />
                        </label>
                      )}
                    </td>
                    <td>
                      <div className="table-actions">
                        <button className="action-btn" onClick={() => openModal(track)}>
                          <Edit size={16} />
                        </button>
                        <button className="action-btn delete" onClick={() => handleDelete(track.id)}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingTrack ? 'Edit Track' : 'Add Track'}</h2>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Release *</label>
                <select
                  value={formData.release_id}
                  onChange={(e) => handleReleaseChange(e.target.value)}
                  required
                >
                  <option value="">Select Release</option>
                  {releases.map(r => (
                    <option key={r.id} value={r.id}>{r.title} - {r.artist_name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Track Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Track Number</label>
                  <input
                    type="number"
                    value={formData.track_number}
                    onChange={(e) => setFormData({...formData, track_number: e.target.value})}
                    min="1"
                  />
                </div>

                <div className="form-group">
                  <label>Duration (seconds)</label>
                  <input
                    type="number"
                    value={formData.duration}
                    onChange={(e) => setFormData({...formData, duration: e.target.value})}
                    min="1"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Preview Start (seconds)</label>
                  <input
                    type="number"
                    value={formData.preview_start}
                    onChange={(e) => setFormData({...formData, preview_start: e.target.value})}
                    min="0"
                  />
                  <span className="hint">Where 30-second preview begins</span>
                </div>

                <div className="form-group">
                  <label>Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    min="0"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Discount Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.discount_price}
                    onChange={(e) => setFormData({...formData, discount_price: e.target.value})}
                    min="0"
                    placeholder="Leave empty for no discount"
                  />
                </div>

                <div className="form-group">
                  <label>Available for Sale</label>
                  <select
                    value={formData.is_available ? 'true' : 'false'}
                    onChange={(e) => setFormData({...formData, is_available: e.target.value === 'true'})}
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingTrack ? 'Update' : 'Create'} Track
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackManagement;
